// src/pages/Reports/HiringCycleReport.js
import React, { useEffect, useState, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import dayjs from "dayjs";
import Select from "../../components/ReportSelect";
import { saveAs } from "file-saver";
import { Container, Button, Row, Col, Spinner } from "react-bootstrap";
import "../../styles/Reports.css";
import DataTable from "../../components/DataTable";

const BASE_URL = process.env.REACT_APP_API_BASE_URL;
// Default date range
const today = dayjs().format("YYYY-MM-DD");
const oneMonthAgo = dayjs().subtract(1, "month").format("YYYY-MM-DD");

const fmt = (d) => (d ? dayjs(d).format("MM/DD/YYYY") : "");
const toISO = (v) => (v ? dayjs(v).format("YYYY-MM-DD") : "");

export default function HiringCycleReport() {
  const navigate = useNavigate();

  // Dropdown sources
  const [orgUnits, setOrgUnits] = useState([]);
  const [recruiters, setRecruiters] = useState([]);
  const [categories, setCategories] = useState([]);
  const [subCats, setSubCats] = useState([]);

  // Table rows
  const [rows, setRows] = useState([]);

  const [loading, setLoading] = useState(false);
  const [hasSearched, setHasSearched] = useState(false);
  // Page-level filter UI inputs
  const [pStartDate, setPStartDate] = useState(oneMonthAgo);
  const [pEndDate, setPEndDate] = useState(today);
  const [pOrgUnit, setPOrgUnit] = useState(null);
  const [pRecruiter, setPRecruiter] = useState(null);
  const [pCategory, setPCategory] = useState(null);
  const [pSubCat, setPSubCat] = useState(null);

  // Actual applied filters used to fetch data
  const [fStartDate, setFStartDate] = useState("");
  const [fEndDate, setFEndDate] = useState("");
  const [fOrgUnit, setFOrgUnit] = useState("");
  const [fRecruiter, setFRecruiter] = useState("");
  const [fCategory, setFCategory] = useState("");
  const [fSubCat, setFSubCat] = useState("");

  // Load dropdowns
  useEffect(() => {
    const loadDropdowns = async () => {
      const token = localStorage.getItem("token");
      const headers = { Authorization: `Bearer ${token}` };

      try {
        const [ou, rec, cat, sub] = await Promise.all([
          axios.get(`${BASE_URL}/api/dropdowns/org-units`, { headers }),
          axios.get(`${BASE_URL}/api/dropdowns/recruiters`, { headers }),
          axios.get(`${BASE_URL}/api/dropdowns/categories`, { headers }),
          axios.get(`${BASE_URL}/api/dropdowns/sub-categories`, { headers }),
        ]);

        setOrgUnits(ou.data || []);
        setRecruiters(rec.data || []); // already formatted {label, value}
        setCategories(cat.data || []);
        setSubCats(sub.data || []);
      } catch (err) {
        console.error("Dropdown load error:", err);
      }
    };

    loadDropdowns();
  }, []);

  // ⭐ FIX: Stable fetchRows using useCallback to prevent ESLint errors
  const fetchRows = useCallback(async () => {
    setLoading(true);

    const token = localStorage.getItem("token");
    const headers = { Authorization: `Bearer ${token}` };

    try {
      const res = await axios.get(`${BASE_URL}/api/reports/hiring-cycle`, {
        headers,
        params: {
          orgUnit: fOrgUnit,
          recruiter: fRecruiter,
          category: fCategory,
          subCategory: fSubCat,
          startDate: fStartDate,
          endDate: fEndDate,
        },
      });
      setRows(res.data || []);
    } catch (err) {
      console.error("Hiring Cycle fetch error:", err);
      setRows([]);
    } finally {
      setLoading(false);
    }
  }, [fOrgUnit, fRecruiter, fCategory, fSubCat, fStartDate, fEndDate]);

  // ⭐ FIX: useEffect now safe — no ESLint warnings, no auto-load, no double fetch
  useEffect(() => {
    if (!hasSearched) return;
    fetchRows();
  }, [hasSearched, fetchRows]);

  // User clicks Filter
  const applyFilters = () => {
    setHasSearched(true);
    setFStartDate(pStartDate);
    setFEndDate(pEndDate);
    setFOrgUnit(pOrgUnit?.value || "");
    setFRecruiter(pRecruiter?.value || "");
    setFCategory(pCategory?.value || "");
    setFSubCat(pSubCat?.value || "");
  };

  // Clear filters
  const clearFilters = () => {
    setPStartDate(oneMonthAgo);
    setPEndDate(today);
    setPOrgUnit(null);
    setPRecruiter(null);
    setPCategory(null);
    setPSubCat(null);

    setFStartDate("");
    setFEndDate("");
    setFOrgUnit("");
    setFRecruiter("");
    setFCategory("");
    setFSubCat("");

    setRows([]);
    setHasSearched(false);
  };

  const columns = [
    { key: "org_unit", label: "Org Unit", cellClassName: "text-center" },
    {
      key: "recruiter",
      label: "Recruiter",
      getSortValue: (row) =>
        `${row.recruiter_first || ""} ${row.recruiter_last || ""}`.trim(),
      cellClassName: "text-center",
      render: (row) =>
        `${row.recruiter_first || ""} ${row.recruiter_last || ""}`.trim(),
    },
    { key: "first_name", label: "First Name", cellClassName: "text-center" },
    { key: "last_name", label: "Last Name", cellClassName: "text-center" },
    { key: "emp_category", label: "Emp Category", cellClassName: "text-center" },
    {
      key: "emp_sub_category",
      label: "Sub Category",
      cellClassName: "text-center",
    },
    { key: "position_full", label: "Position", cellClassName: "text-center" },
    {
      key: "date_added",
      label: "Date Added",
      cellClassName: "text-center",
      render: (row) => fmt(row.date_added),
    },
    {
      key: "date_hired",
      label: "Date Hired",
      cellClassName: "text-center",
      render: (row) => fmt(row.date_hired),
    },
    { key: "hiring_days", label: "Hiring Days", cellClassName: "text-center" },
  ];

  // Export CSV
  const exportCSV = () => {
    if (!hasSearched || rows.length === 0) {
      alert("No data to export");
      return;
    }

    const headers = [
      "Org Unit",
      "Recruiter",
      "Last Name",
      "First Name",
      "Emp Category",
      "Sub Category",
      "Position",
      "Date Added",
      "Date Hired",
      "Hiring Days",
    ];

    const lines = rows.map((r) => {
      const cols = [
        r.org_unit || "",
        `${r.recruiter_first || ""} ${r.recruiter_last || ""}`.trim(),
        r.last_name || "",
        r.first_name || "",
        r.emp_category || "",
        r.emp_sub_category || "",
        r.position_full || "",
        fmt(r.date_added),
        fmt(r.date_hired),
        r.hiring_days || "",
      ];

      return cols.map((c) => `"${String(c).replace(/"/g, '""')}"`).join(",");
    });

    const blob = new Blob([[headers.join(","), ...lines].join("\n")], {
      type: "text/csv;charset=utf-8",
    });

    saveAs(blob, "HiringCycleReport.csv");
  };

  return (
    <Container className="form-wrapper my-4">
      {/* Header */}
      <div className="d-flex justify-content-between align-items-center border-bottom pb-2 mb-4">
        <h3 className="report-page-title fw-bold m-0"><span className="report-title-icon" aria-hidden="true"><i className="fas fa-chart-line" /></span><span>Hiring Cycle</span></h3>
        <Button
          variant="outline-secondary"
          size="sm"
          onClick={() => navigate("/reports")}
        >
          ⬅ Return to Report List
        </Button>
      </div>

      {/* Filter Row 1 */}
      <Row className="mb-3 g-3 align-items-end report-row">
        <Col>
          <label>Start Date</label>
          <input
            type="date"
            className="form-control"
            value={pStartDate}
            onChange={(e) => setPStartDate(toISO(e.target.value))}
          />
        </Col>

        <Col>
          <label>End Date</label>
          <input
            type="date"
            className="form-control"
            value={pEndDate}
            onChange={(e) => setPEndDate(toISO(e.target.value))}
          />
        </Col>

        <Col>
          <label>Org Unit</label>
          <Select
            options={orgUnits.map((o) => ({
              value: o.ou_id,
              label: o.ou_name,
            }))}
            value={pOrgUnit}
            onChange={setPOrgUnit}
          />
        </Col>

        <Col>
          <label>Recruiter</label>
          <Select
            options={recruiters}
            value={pRecruiter}
            onChange={setPRecruiter}
          />
        </Col>

        <Col>
          <label>Category</label>
          <Select
            options={categories.map((c) => ({
              value: c.c_id,
              label: c.c_name,
            }))}
            value={pCategory}
            onChange={setPCategory}
          />
        </Col>

        <Col>
          <label>Sub Category</label>
          <Select
            options={subCats.map((sc) => ({
              value: sc.sc_id,
              label: sc.sc_name,
            }))}
            value={pSubCat}
            onChange={setPSubCat}
          />
        </Col>
      </Row>

      {/* Filter buttons */}
      <Row className="mb-3 g-3 align-items-end report-row report-actions-row">
        <Col md="auto">
          <div className="button-group">
            <Button variant="primary" onClick={applyFilters}>
              <i className="fas fa-filter" aria-hidden="true" /> Filter
            </Button>
            <Button variant="secondary" onClick={clearFilters}>
              Clear Filter
            </Button>
          </div>
        </Col>

        <Col md="auto" className="ms-auto">
          <Button
            className="btn-export"
            onClick={exportCSV}
            disabled={!hasSearched || rows.length === 0}
          >
            Export CSV
          </Button>
        </Col>
      </Row>

      {/* Table */}
      {!hasSearched ? (
        <div className="text-center text-muted py-5">
          Use the filters above and click <strong>Filter</strong> to load
          results.
        </div>
      ) : loading ? (
        <div className="text-center my-5">
          <Spinner animation="border" />
        </div>
      ) : (
        <DataTable
          columns={columns}
          data={rows}
          rowKey={(row, index) => `${row.id || row.first_name || "hire"}-${index}`}
          itemsPerPage={10}
          tableClassName="compact-table"
          emptyMessage="No results found with current filters."
        />
      )}
    </Container>
  );
}
